import { useEffect, useRef, useState } from "react";
import { BRAND_COLORS } from "@/lib/menuData";

/**
 * Sticky horizontal category bar shown beneath the concept tabs.
 * - Auto-detects which category section is currently in view (IntersectionObserver)
 *   and underlines its label.
 * - Click a label to smooth-scroll to that section.
 * - Categories are passed in as { id, label, sectionId } objects.
 */
export default function CategoryTabBar({ categories, stickyTop = 64, className = "" }) {
  const [activeId, setActiveId] = useState(categories[0]?.sectionId);
  const scrollRef = useRef(null);
  const skipObserverUntil = useRef(0);

  // Observe section visibility to drive the underline
  useEffect(() => {
    if (!categories?.length) return;

    const handler = (entries) => {
      // Skip observer updates briefly after a manual click so the click-target
      // doesn't get overridden by the scroll passing through other sections.
      if (Date.now() < skipObserverUntil.current) return;
      // Pick the entry closest to the top of the viewport (after stickyTop)
      const visible = entries
        .filter((e) => e.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      if (visible.length > 0) {
        setActiveId(visible[0].target.id);
      }
    };

    const observer = new IntersectionObserver(handler, {
      rootMargin: `-${stickyTop + 40}px 0px -55% 0px`,
      threshold: [0, 0.1, 0.5],
    });

    const targets = [];
    categories.forEach((c) => {
      const el = document.getElementById(c.sectionId);
      if (el) {
        observer.observe(el);
        targets.push(el);
      }
    });

    return () => {
      targets.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [categories, stickyTop]);

  // When active tab changes, scroll the tab bar so the pill is centered.
  useEffect(() => {
    if (!scrollRef.current || !activeId) return;
    const el = scrollRef.current.querySelector(`[data-cat-tab="${activeId}"]`);
    if (el) {
      const container = scrollRef.current;
      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const offset = elRect.left - containerRect.left - containerRect.width / 2 + elRect.width / 2;
      container.scrollBy({ left: offset, behavior: "smooth" });
    }
  }, [activeId]);

  const handleClick = (sectionId) => {
    setActiveId(sectionId);
    skipObserverUntil.current = Date.now() + 900;
    const el = document.getElementById(sectionId);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - stickyTop - 12;
    window.scrollTo({ top, behavior: "smooth" });
  };

  if (!categories?.length) return null;

  return (
    <div
      ref={scrollRef}
      data-testid="category-tab-bar"
      className={`category-bar sticky z-20 bg-slate-50 border-b flex overflow-x-auto px-1 ${className}`}
      style={{ top: stickyTop, borderColor: "#E5E7EB", gap: 0 }}
    >
      {categories.map((c) => {
        const active = c.sectionId === activeId;
        return (
          <button
            key={c.sectionId}
            data-cat-tab={c.sectionId}
            data-testid={`cat-tab-${c.id}`}
            onClick={() => handleClick(c.sectionId)}
            className="cat-tab flex-shrink-0 px-3.5 py-2.5 text-[12px] font-medium whitespace-nowrap transition-all"
            style={{
              color: active ? BRAND_COLORS.darkSlate : "#6b7280",
              borderBottom: active ? `2px solid ${BRAND_COLORS.darkSlate}` : "2px solid transparent",
              background: "transparent",
              cursor: "pointer",
            }}
          >
            {c.label}
          </button>
        );
      })}
    </div>
  );
}
